import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SeoHelmet, { faqSchema, breadcrumbSchema } from "./SeoHelmet";
import Breadcrumbs from "./Breadcrumbs";
import Faq from "./Faq";
import { useAuth } from "../context/AuthContext";

/**
 * Standard SEO page shell.
 * Pass props for breadcrumbs, hero, sections, faqs, related links, and CTA.
 * Sections support: heading, body (string or array of paragraphs), bullets, sub-headings.
 */
export default function SeoPage({
    title,
    description,
    breadcrumbs = [],
    h1,
    eyebrow = "/ resource",
    heroLead,
    heroBullets = [],
    sections = [],
    faqs = [],
    related = [],
    ctaTitle = "Open, edit, and share CSVs in seconds.",
    ctaSub = "Free to view, $9.99/mo to edit. 14-day free trial.",
}) {
    const { user } = useAuth();
    const isPaid = !!user && (user.is_paid || user.role === "admin");
    const schemas = [
        breadcrumbs.length > 0 ? breadcrumbSchema(breadcrumbs.map((b) => ({ label: b.label, href: b.href || "/" }))) : null,
        faqs.length > 0 ? faqSchema(faqs) : null,
    ].filter(Boolean);

    return (
        <>
            <SeoHelmet title={title} description={description} schema={schemas} />
            <article>
                <header className="border-b border-black/10">
                    <div className="mx-auto max-w-4xl px-4 md:px-8 pt-6 pb-12 md:pb-20">
                        {breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />}
                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mt-2">{eyebrow}</div>
                        <h1 className="mt-3 font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.95]">
                            {h1}
                        </h1>
                        {heroLead && <p className="mt-5 text-base md:text-lg text-ink-soft max-w-2xl leading-relaxed">{heroLead}</p>}
                        {heroBullets.length > 0 && (
                            <ul className="mt-6 grid sm:grid-cols-2 gap-2 max-w-2xl text-sm text-ink-soft">
                                {heroBullets.map((b, i) => (
                                    <li key={i} className="flex items-start gap-2"><Check size={14} className="text-cobalt mt-1 shrink-0" /> {b}</li>
                                ))}
                            </ul>
                        )}
                        <div className="mt-8 flex flex-wrap gap-3">
                            {isPaid ? (
                                <Link to="/app" className="btn-brutal inline-flex items-center gap-2 bg-cobalt hover:bg-cobalt-hover text-white px-5 py-3 ui-heading text-sm font-semibold border border-cobalt" data-testid="seo-cta-open-editor">
                                    Open the editor <ArrowRight size={14} />
                                </Link>
                            ) : (
                                <>
                                    <Link to="/app" className="btn-brutal inline-flex items-center gap-2 bg-cobalt hover:bg-cobalt-hover text-white px-5 py-3 ui-heading text-sm font-semibold border border-cobalt" data-testid="seo-cta-signup">
                                        Open the free viewer <ArrowRight size={14} />
                                    </Link>
                                    <Link to="/pricing" className="btn-brutal inline-flex items-center gap-2 bg-white text-ink px-5 py-3 ui-heading text-sm font-semibold border border-ink/20" data-testid="seo-cta-try">
                                        See Pro plans
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </header>

                <div className="mx-auto max-w-4xl px-4 md:px-8 py-12 space-y-14">
                    {sections.map((s, i) => (
                        <section key={i} data-testid={`seo-section-${i}`}>
                            {s.heading && <h2 className="font-display font-black tracking-tighter text-2xl md:text-3xl mb-4">{s.heading}</h2>}
                            {Array.isArray(s.body)
                                ? s.body.map((p, j) => <p key={j} className="text-base text-ink-soft leading-relaxed mb-3">{p}</p>)
                                : s.body && <p className="text-base text-ink-soft leading-relaxed">{s.body}</p>}
                            {s.bullets && (
                                <ul className="mt-4 space-y-2 text-sm text-ink-soft">
                                    {s.bullets.map((b, j) => (
                                        <li key={j} className="flex items-start gap-2"><Check size={14} className="text-cobalt mt-1 shrink-0" /><span>{b}</span></li>
                                    ))}
                                </ul>
                            )}
                            {s.sub && s.sub.map((sb, j) => (
                                <div key={j} className="mt-6">
                                    <h3 className="ui-heading font-bold text-lg text-ink">{sb.heading}</h3>
                                    {Array.isArray(sb.body)
                                        ? sb.body.map((p, k) => <p key={k} className="text-sm text-ink-soft leading-relaxed mt-2">{p}</p>)
                                        : sb.body && <p className="text-sm text-ink-soft leading-relaxed mt-2">{sb.body}</p>}
                                    {sb.bullets && (
                                        <ul className="mt-2 space-y-1.5 text-sm text-ink-soft">
                                            {sb.bullets.map((b, k) => (
                                                <li key={k} className="flex items-start gap-2"><Check size={12} className="text-cobalt mt-1.5 shrink-0" /><span>{b}</span></li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                            {s.callout && (
                                <div className="mt-5 border border-cobalt/30 bg-cobalt/5 p-4 text-sm text-ink-soft" data-testid={`callout-${i}`}>{s.callout}</div>
                            )}
                            {s.table && (
                                <div className="mt-5 border border-black/10 overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-paper border-b border-black/10">
                                            <tr>{s.table.headers.map((h, j) => <th key={j} className="px-4 py-2 text-left ui-heading text-xs uppercase tracking-wider">{h}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            {s.table.rows.map((r, j) => (
                                                <tr key={j} className="border-b border-black/5">
                                                    {r.map((c, k) => <td key={k} className="px-4 py-2.5 align-top">{c}</td>)}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    ))}
                </div>

                {faqs.length > 0 && <Faq items={faqs} testIdPrefix={`faq-${title.toLowerCase().replace(/\s+/g, '-')}`} />}

                {related.length > 0 && (
                    <section className="border-t border-black/10 bg-paper">
                        <div className="mx-auto max-w-4xl px-4 md:px-8 py-12">
                            <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">/ keep reading</div>
                            <h2 className="font-display font-black tracking-tighter text-2xl mb-6">Related guides</h2>
                            <ul className="grid sm:grid-cols-2 gap-3">
                                {related.map((r, i) => (
                                    <li key={i}>
                                        <Link to={r.href} className="block border border-black/10 bg-white p-4 hover:border-cobalt transition-colors" data-testid={`related-${i}`}>
                                            <div className="ui-heading text-xs uppercase tracking-widest text-cobalt">{r.eyebrow || "/ guide"}</div>
                                            <div className="ui-heading font-semibold text-ink mt-1">{r.label}</div>
                                            {r.summary && <p className="text-xs text-ink-muted mt-1">{r.summary}</p>}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>
                )}

                <section className="border-t border-black/10">
                    <div className="mx-auto max-w-4xl px-4 md:px-8 py-16 text-center">
                        <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl">{isPaid ? "Already in. Let's go." : ctaTitle}</h2>
                        <p className="mt-3 text-ink-soft">{isPaid ? "You're all set — open the editor and start working." : ctaSub}</p>
                        {isPaid ? (
                            <Link to="/app" className="btn-brutal inline-flex items-center gap-2 mt-6 bg-cobalt text-white px-6 py-3 ui-heading text-sm font-semibold border border-cobalt" data-testid="seo-cta-bottom-app">
                                Open the editor <ArrowRight size={14} />
                            </Link>
                        ) : (
                            <Link to="/pricing" className="btn-brutal inline-flex items-center gap-2 mt-6 bg-ink text-white px-6 py-3 ui-heading text-sm font-semibold border border-ink" data-testid="seo-cta-bottom">
                                Start 14-day free trial <ArrowRight size={14} />
                            </Link>
                        )}
                    </div>
                </section>
            </article>
        </>
    );
}
